/**
 * 画像プレビューモーダル（MediaPreviewModal の fitToImage）の表示サイズ計算。
 *
 * 画像を開いた時点でビューポートに応じた最大幅（元画像の幅が上限）で表示する
 * 方針。モーダル（Modal size="auto-viewport"）の余白・枠線・タイトル行を除いた
 * 「画像に使える幅」を求める純粋関数。縦に収まらない分はスクロールで見せる
 * ため、高さはここでは制約しない（幅だけで決まる）。
 * 定数は Modal.tsx / MediaPreviewModal.tsx の実値と対応させる。
 */

/** モーダル外周の上下余白（Modal Content: max-height: calc(100dvh - 32px)） */
const VIEWPORT_MARGIN_Y_PX = 32;

/** Modal Content の上下枠線（border 1px × 2） */
const MODAL_FRAME_Y_PX = 2;

/**
 * DialogHeader（titleSize="compact"）の高さ。
 * padding-top 12px + タイトル 1 行（bodytext-13: 13px × line-height 200% = 26px）
 * + padding-bottom 8px。長いファイル名で折り返すと実際はこれより高くなるが、
 * その分は Body のスクロールで逃がす（Modal の max-height が全体をクランプする）。
 */
const MODAL_HEADER_PX = 46;

/** Modal Body の下 padding（タイトルあり・フッターなしのとき上 0 / 下 24px） */
const MODAL_BODY_PADDING_Y_PX = 24;

/**
 * PreviewArea（画像 + メニュー行のスクロール領域）の外にある縦方向の合計。
 * PreviewArea の max-height: calc(100dvh - この値 px) に使う。
 */
export const PREVIEW_AREA_CHROME_PX =
  VIEWPORT_MARGIN_Y_PX +
  MODAL_FRAME_Y_PX +
  MODAL_HEADER_PX +
  MODAL_BODY_PADDING_Y_PX;

/** モーダル外周の左右余白（Modal size="auto-viewport": max-width: calc(100vw - 32px)） */
const VIEWPORT_MARGIN_X_PX = 32;

/** Modal 本文の左右 padding（12px × 2）+ 枠線（1px × 2） */
const MODAL_BODY_INSET_PX = 26;

/** 画像の表示に使えない横方向の合計 */
export const PREVIEW_HORIZONTAL_CHROME_PX =
  VIEWPORT_MARGIN_X_PX + MODAL_BODY_INSET_PX;

export interface ImageFitResult {
  /** 画像の表示幅（CSS px）。等倍表示ではこれではなく元画像の幅を使う */
  width: number;
  /** クリックで等倍表示へ切り替える意味があるか（表示幅 < 元画像の幅） */
  canZoom: boolean;
}

/**
 * 元画像の幅とビューポート幅から、画像プレビューの表示幅を決める。
 *
 * - ビューポートに応じた最大幅まで広げる（元画像より大きくは引き伸ばさない）
 * - 元画像がビューポートよりさらに広い場合のみ canZoom を立て、クリックで
 *   等倍（100%・横スクロール）と最大幅表示を切り替えられるようにする
 */
export const computeImageFit = (
  naturalWidth: number,
  viewportWidth: number,
): ImageFitResult => {
  const availableWidth = Math.max(1, viewportWidth - PREVIEW_HORIZONTAL_CHROME_PX);
  const width = Math.max(1, Math.floor(Math.min(naturalWidth, availableWidth)));
  return { width, canZoom: width < naturalWidth };
};
